"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/layanan/jmc-skincare/ - tempat berkas INI
   berada.
     ../../components, ../../i18n  -> naik dua tingkat ke app/
     ./jmc-skincare                -> tetangga di folder yang sama */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import {
  CATATAN_PERAWATAN,
  FASILITAS,
  FASILITAS_PENUTUP,
  KETENTUAN,
  LOKASI,
  PERAWATAN,
  POSTER,
  SKINCARE,
} from "./jmc-skincare";

/* ============================================================
   JMC SKINCARE - /layanan/jmc-skincare

   Sebelum berkas ini ada, alamat ini 404 - padahal sudah ditunjuk
   menu Header dan halaman /layanan.

   ============================================================
   SATU HAL YANG MEMBEDAKAN HALAMAN INI DARI SEMUA HALAMAN LAYANAN
   LAIN: ALAMATNYA BUKAN DI GEDUNG RUMAH SAKIT.

   Empat belas layanan lain berada di Jl. Warung Buncit Raya No. 15.
   Yang ini di Lantai 6 Suite Room.

   Di situs lama, keterangan itu hanya ada DI DALAM GAMBAR POSTER -
   tidak terbaca pembaca layar, tidak terbaca mesin pencari, dan tidak
   terbaca siapa pun yang gambarnya gagal dimuat. Akibatnya orang
   datang ke rumah sakit lalu diberi tahu bahwa tempatnya di gedung
   lain.

   Karena itu di halaman ini lokasi TIDAK ditaruh di dasar halaman
   bersama keterangan lain. Ia diangkat ke kepala halaman, tepat di
   bawah judul, sebagai kotak tersendiri - sebelum tombol kontak,
   sebelum poster, sebelum apa pun.

   Urutan itu disengaja: orang harus tahu ini tempat yang berbeda
   SEBELUM ia memutuskan menghubungi.

   ============================================================
   NOMORNYA JUGA BERBEDA

   0851-6886-7636 adalah nomor JMC Skincare sendiri, bukan nomor CS
   rumah sakit. Halaman ini hanya menampilkan nomor itu - lihat
   catatan di jmc-skincare.js.

   ============================================================
   POSTERNYA DUA LEMBAR, DAN TIDAK DIKUMPULKAN DI SATU TEMPAT

   Sebelumnya posternya satu lembar roll-up berperbandingan 1 : 2,9,
   ditaruh di kolom kanan kepala halaman. Hasilnya dua masalah
   sekaligus:

     - tingginya melebihi 1500px di kolom selebar 520px, sementara
       kolom kiri selesai di sepertiga tingginya. Bidang kosong besar
       menganga di kiri-bawah.
     - karena diperkecil sebanyak itu, tulisan di dalamnya tidak
       terbaca. Poster yang tidak terbaca sama saja dengan tidak ada.

   Sekarang dua lembar berperbandingan 4:5, dan masing-masing
   DITARUH DI BAGIAN YANG ISINYA SESUAI:

     lembar 1 (Perawatan Unggulan)  -> kolom kanan kepala halaman
     lembar 2 (Daftar Fasilitas)    -> di samping daftar fasilitas

   Bukan dikumpulkan jadi galeri "Poster" tersendiri di dasar
   halaman. Alasannya: poster di sini bukan lampiran, melainkan versi
   cetak dari bagian yang sedang dibaca. Menaruhnya bersebelahan
   membuat keduanya saling menguatkan; menaruhnya di dasar halaman
   membuat orang harus mengingat-ingat isi bagian atas.

   Tidak ada lembar yang ditampilkan dua kali.

   ============================================================
   className ditulis satu baris - lihat peringatan hydration di
   Facility.jsx. Jangan pakai string multi-baris di berkas ini.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

/* Halaman induk /layanan sudah ada, jadi remah rotinya boleh jadi
   tautan. Pola dan alasannya sama dengan halaman layanan lain. */
const TAUTKAN_INDUK = true;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "JMC Skincare", en: "JMC Skincare" },
  sub: {
    id: "Perawatan kulit dan estetika medis di bawah naungan RS Jakarta Medical Center.",
    en: "Skin care and medical aesthetics under Jakarta Medical Center Hospital.",
  },

  lokasiLabel: { id: "Lokasi", en: "Location" },
  lihatPeta: { id: "Buka di Google Maps", en: "Open in Google Maps" },

  hubungi: { id: "Hubungi lewat WhatsApp", en: "Contact on WhatsApp" },

  nomorJudul: { id: "Informasi dan Reservasi", en: "Information and Booking" },
  nomorSub: {
    id: "Perawatan yang tersedia, jadwal, dan biayanya dapat ditanyakan langsung ke Customer Service JMC Skincare.",
    en: "Ask JMC Skincare's customer service directly about available treatments, schedules, and pricing.",
  },
  chatWa: { id: "Chat WhatsApp", en: "Chat on WhatsApp" },
  telepon: { id: "Telepon", en: "Call" },

  perawatanJudul: { id: "Perawatan Unggulan", en: "Featured Treatments" },
  perawatanSub: {
    id: "Tiga perawatan yang paling sering ditanyakan. Daftar lengkapnya ada di bawah.",
    en: "The three most frequently asked-about treatments. The full list is below.",
  },

  fasilitasJudul: { id: "Fasilitas & Perawatan Lain", en: "Other Facilities & Treatments" },
  fasilitasSub: {
    id: "Perawatan wajah, tubuh, rambut, serta tangan dan kaki yang tersedia di JMC Skincare.",
    en: "Face, body, hair, and hand and foot treatments available at JMC Skincare.",
  },

  ketentuanJudul: { id: "Ketentuan Layanan", en: "Service Details" },

  perbesar: { id: "Klik untuk memperbesar", en: "Click to enlarge" },
  tutup: { id: "Tutup", en: "Close" },
  sebelumnya: { id: "Sebelumnya", en: "Previous" },
  berikutnya: { id: "Berikutnya", en: "Next" },

  lihatLayanan: { id: "Lihat layanan lainnya", en: "See other services" },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

export default function JmcSkincareContent() {
  const { lang, t } = useLanguage();

  /* Nama berkas poster diisi manual di jmc-skincare.js dan
     ekstensinya mudah meleset. Yang gagal dimuat dicatat per src,
     BUKAN satu saklar untuk semuanya - kalau lembar 2 salah nama,
     lembar 1 tetap harus tampil. */
  const [gagal, setGagal] = useState([]);
  const tandaiGagal = (src) => setGagal((s) => (s.includes(src) ? s : [...s, src]));

  const poster = (POSTER ?? []).filter((p) => p?.src && !gagal.includes(p.src));

  /* Indeks lembar yang sedang dibuka besar. null = tertutup. */
  const [pratinjau, setPratinjau] = useState(null);

  const utama = SKINCARE.telepon.find((n) => n.utama) ?? SKINCARE.telepon[0];

  const gedung = pilihTeks(LOKASI?.gedung, lang);
  const alamat = pilihTeks(LOKASI?.alamat, lang);

  /* Tombol peta muncul hanya kalau `cari` diisi. Memakai pencarian
     berdasarkan nama dan alamat, bukan koordinat - kalau titiknya
     digeser di Google Maps, tautannya tetap benar. */
  const peta = LOKASI?.cari
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(LOKASI.cari)}`
    : null;

  /* WhatsApp hanya MENGISI kolom ketik, tidak mengirim - pengunjung
     masih bisa menyunting pesannya sebelum menekan kirim. */
  function tautanWa(n) {
    const pesan = pilihTeks(n.pesanWa, lang);
    return `https://wa.me/${n.wa}?text=${encodeURIComponent(pesan)}`;
  }

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div className="mx-auto max-w-[1600px] px-4 py-3 sm:px-6 sm:py-3.5 lg:px-10">
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA: DUA KOLOM ----------
         Kolom kanan sekarang berisi LEMBAR PERTAMA saja, bukan
         seluruh roll-up. Perbandingannya 4:5, jadi tingginya sepadan
         dengan kolom kiri dan `items-center` benar-benar bekerja.

         Kalau posternya tidak ada - berkasnya belum diunggah atau
         namanya salah - gridnya menyusut sendiri jadi satu kolom
         lewat auto-fit, bukan menyisakan kolom kosong. */}
      <div className="bg-[var(--c-tint)]">
        <div className="mx-auto px-4 py-12 md:py-14" style={{ maxWidth: LEBAR_HALAMAN }}>
          <div className="grid items-center gap-10" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))" }}>

            <div className="min-w-0">
              <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]">
                {pilihTeks(TEKS.eyebrow, lang)}
              </p>

              <h1 className="mt-2.5 text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ fontSize: "clamp(30px, 4.4vw, 44px)" }}>
                {pilihTeks(TEKS.judul, lang)}
              </h1>

              <p className="mt-4 max-w-[520px] text-[15.5px] leading-[1.7] text-[var(--c-body)]">
                {pilihTeks(TEKS.sub, lang)}
              </p>

              {/* ---------- LOKASI ----------
                 DITARUH SEBELUM TOMBOL KONTAK, dan itu keputusan yang
                 paling penting di halaman ini.

                 Alamatnya berbeda dari gedung rumah sakit. Orang harus
                 tahu itu SEBELUM memutuskan menghubungi - bukan setelah
                 ia berangkat ke alamat yang salah.

                 Yang membuatnya menonjol adalah posisinya, bukan
                 warnanya. Ini bukan bahaya, hanya keterangan yang
                 mudah terlewat.

                 ⚠️ SOAL LEBARNYA - `w-fit`, BUKAN LEBAR PENUH.

                 <div> itu block-level: dengan max-w-[520px] saja ia
                 SELALU melar sampai 520px, berapa pun isinya. Selama
                 LOKASI.alamat masih kosong dan tombol peta belum
                 muncul, isinya cuma satu baris - hasilnya kotak lebar
                 yang isinya sepertiga, terbaca sebagai layout rusak,
                 bukan sebagai penekanan.

                 `w-fit` membuatnya menyusut mengikuti isi;
                 max-w-[520px] tetap ada sebagai pagar untuk saat
                 alamatnya sudah diisi. Di layar sempit w-fit tidak
                 akan melebihi lebar kolomnya, jadi aman.

                 Kalau nanti alamat sudah diisi dan kamu mau kotak ini
                 kembali menonjol, hapus `w-fit` - JANGAN tambah
                 warna. Alasannya ada di paragraf di atas. */}
              {gedung && (
                <div className="mt-5 w-fit max-w-[520px] rounded-2xl bg-[var(--c-surface)] px-4 py-3.5 ring-1 ring-[var(--c-brand-line)]">
                  <p className="flex items-start gap-2.5">
                    <IkonPeta />
                    <span className="min-w-0">
                      <span className="block text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[var(--c-brand-mid)]">
                        {pilihTeks(TEKS.lokasiLabel, lang)}
                      </span>
                      <span className="mt-0.5 block text-[15.5px] font-bold text-[var(--c-ink)]">{gedung}</span>
                      {alamat && (
                        <span className="mt-1 block text-[14px] leading-relaxed text-[var(--c-body)]">{alamat}</span>
                      )}
                    </span>
                  </p>

                  {peta && (
                    <a
                      href={peta}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2.5 inline-flex items-center gap-1.5 text-[13.5px] font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                    >
                      {pilihTeks(TEKS.lihatPeta, lang)}
                      <span aria-hidden="true">&rarr;</span>
                    </a>
                  )}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                {utama.wa && (
                  <a
                    href={tautanWa(utama)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 rounded-[12px] bg-[var(--c-brand)] px-6 py-4 text-[16px] font-bold text-white shadow-sm transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                  >
                    <IkonWhatsApp />
                    {pilihTeks(TEKS.hubungi, lang)}
                  </a>
                )}

                {/* Nomor yang SAMA lewat jalur telepon. Nomornya sendiri
                   yang jadi tulisan tombol - orang yang membuka dari
                   komputer perlu MEMBACA angkanya untuk ditekan di
                   ponsel lain. */}
                <a
                  href={`tel:${utama.tel}`}
                  className="inline-flex items-center gap-2.5 rounded-[12px] border border-[var(--c-brand-300)] bg-[var(--c-surface)] px-6 py-4 text-[16px] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                >
                  <IkonTelepon />
                  {utama.tampil}
                </a>
              </div>
            </div>

            {poster[0] && (
              <div className="min-w-0 lg:justify-self-end">
                <KartuPoster
                  poster={poster[0]}
                  lang={lang}
                  prioritas
                  lebarMaks={480}
                  ukuran="(min-width: 1024px) 480px, 92vw"
                  onBuka={() => setPratinjau(0)}
                  onGagal={() => tandaiGagal(poster[0].src)}
                />
              </div>
            )}

          </div>
        </div>
      </div>

      <div className="mx-auto px-4 py-12 md:py-16" style={{ maxWidth: LEBAR_HALAMAN }}>

        {/* ---------- PERAWATAN ----------
           Muncul hanya kalau PERAWATAN diisi. Lihat catatan di
           jmc-skincare.js soal cara menulis keterangannya - apa
           tindakannya, bukan hasil yang dijanjikannya. */}
        {PERAWATAN.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
              {pilihTeks(TEKS.perawatanJudul, lang)}
            </h2>
            <p className="mt-2 max-w-[640px] text-[15px] leading-relaxed text-[var(--c-body)]">
              {pilihTeks(TEKS.perawatanSub, lang)}
            </p>

            <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PERAWATAN.map((p, i) => (
                <li key={i} className="rounded-2xl bg-[var(--c-surface)] p-5 ring-1 ring-[var(--c-line)]">
                  <p className="text-[15.5px] font-bold text-[var(--c-ink)]">{pilihTeks(p.nama, lang)}</p>
                  <p className="mt-1.5 text-[14.5px] leading-relaxed text-[var(--c-body)]">{pilihTeks(p.isi, lang)}</p>
                </li>
              ))}
            </ul>

            {/* Catatan pembatas hasil. Kecil, tapi WAJIB ADA di
               halaman perawatan estetika - lihat alasannya di
               jmc-skincare.js. Jangan dihapus tanpa meminta
               pendapat dokter penanggung jawab. */}
            {CATATAN_PERAWATAN && (
              <p className="mt-4 text-[13.5px] leading-relaxed text-[var(--c-muted)]">
                {pilihTeks(CATATAN_PERAWATAN, lang)}
              </p>
            )}
          </section>
        )}

        {/* ---------- FASILITAS ----------
           Daftar nama layanan dari poster, DIDAMPINGI lembar kedua
           posternya.

           Susunannya dua kolom di layar lebar: daftar teks di kiri
           (lebih lebar), poster di kanan. Di bawah lg keduanya
           menumpuk - poster di bawah daftarnya, bukan di atas,
           supaya yang terbaca mesin pencari dan pembaca layar tetap
           didahulukan.

           Kolom daftarnya sendiri masih memakai CSS columns: tiga
           kolom kalau posternya tidak ada, dua kalau ada. */}
        {FASILITAS.length > 0 && (
          <section className={PERAWATAN.length > 0 ? "mt-12 md:mt-16" : ""}>
            <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
              {pilihTeks(TEKS.fasilitasJudul, lang)}
            </h2>
            <p className="mt-2 max-w-[640px] text-[15px] leading-relaxed text-[var(--c-body)]">
              {pilihTeks(TEKS.fasilitasSub, lang)}
            </p>

            <div className={poster[1] ? "mt-5 grid items-start gap-6 lg:grid-cols-[1.55fr_1fr]" : "mt-5"}>
              <div className="rounded-2xl bg-[var(--c-surface)] p-5 ring-1 ring-[var(--c-line)] md:p-6">
                <ul className={poster[1] ? "gap-x-8 sm:columns-2" : "gap-x-8 sm:columns-2 lg:columns-3"}>
                  {FASILITAS.map((f, i) => (
                    <li key={i} className="mb-2.5 flex items-start gap-2.5 break-inside-avoid text-[14.5px] leading-relaxed text-slate-700">
                      <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--c-brand-mid)]" />
                      <span>{pilihTeks(f, lang)}</span>
                    </li>
                  ))}
                </ul>

                {/* "dan lain sebagainya" dari poster - ditulis sebagai
                   kalimat, bukan butir daftar, supaya jelas daftarnya
                   tidak lengkap dan orang tetap bertanya. */}
                {FASILITAS_PENUTUP && (
                  <p className="mt-3 border-t border-[var(--c-line)] pt-4 text-[13.5px] leading-relaxed text-[var(--c-muted)]">
                    {pilihTeks(FASILITAS_PENUTUP, lang)}
                  </p>
                )}
              </div>

              {poster[1] && (
                <KartuPoster
                  poster={poster[1]}
                  lang={lang}
                  lebarMaks={400}
                  ukuran="(min-width: 1024px) 400px, 92vw"
                  onBuka={() => setPratinjau(1)}
                  onGagal={() => tandaiGagal(poster[1].src)}
                />
              )}
            </div>
          </section>
        )}

        {/* ---------- KETENTUAN ---------- */}
        {KETENTUAN.length > 0 && (
          <section className={PERAWATAN.length > 0 || FASILITAS.length > 0 ? "mt-12 md:mt-16" : ""}>
            <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
              {pilihTeks(TEKS.ketentuanJudul, lang)}
            </h2>

            {/* <dl>, bukan tabel. Pasangan label-nilai memang itulah
               tempatnya, dan pembaca layar mengumumkannya sebagai
               pasangan keterangan. */}
            <dl className="mt-5 overflow-hidden rounded-2xl ring-1 ring-[var(--c-line)]">
              {KETENTUAN.map((k, i) => (
                <div key={i} className={`flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 bg-[var(--c-surface)] px-5 py-4 ${i > 0 ? "border-t border-[var(--c-line)]" : ""}`}>
                  <dt className="text-[14px] font-semibold text-[var(--c-muted)]">{pilihTeks(k.label, lang)}</dt>
                  <dd className="text-[15px] font-bold text-[var(--c-ink)]">{pilihTeks(k.nilai, lang)}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* ---------- NOMOR ---------- */}
        <section className={PERAWATAN.length > 0 || FASILITAS.length > 0 || KETENTUAN.length > 0 ? "mt-12 md:mt-16" : ""}>
          <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
            {pilihTeks(TEKS.nomorJudul, lang)}
          </h2>
          <p className="mt-2 max-w-[640px] text-[15px] leading-relaxed text-[var(--c-body)]">
            {pilihTeks(TEKS.nomorSub, lang)}
          </p>

          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {SKINCARE.telepon.map((n) => (
              <li key={n.tel} className="rounded-2xl bg-[var(--c-surface)] p-5 ring-1 ring-[var(--c-line)]">
                <p className="text-[13px] font-semibold uppercase tracking-wider text-[var(--c-muted)]">
                  {pilihTeks(n.label, lang)}
                </p>
                <p className="mt-1.5 text-lg font-bold text-[var(--c-ink)]">{n.tampil}</p>

                <div className="mt-3.5 flex flex-wrap gap-2">
                  {n.wa && (
                    <a
                      href={tautanWa(n)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--c-brand)] px-3.5 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                    >
                      <IkonWhatsApp kecil />
                      {pilihTeks(TEKS.chatWa, lang)}
                    </a>
                  )}

                  <a
                    href={`tel:${n.tel}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-bold text-[var(--c-brand)] ring-1 ring-[var(--c-line)] transition-colors hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                  >
                    <IkonTelepon kecil />
                    {pilihTeks(TEKS.telepon, lang)}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-12 md:mt-16">
          <Link
            href="/layanan"
            className="inline-flex items-center gap-2 text-[15px] font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-brand-mid)]"
          >
            {pilihTeks(TEKS.lihatLayanan, lang)}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

      </div>

      <Footer />

      {pratinjau != null && poster[pratinjau] && (
        <Pratinjau
          daftar={poster}
          indeks={pratinjau}
          lang={lang}
          onTutup={() => setPratinjau(null)}
          onPindah={(i) => setPratinjau(i)}
        />
      )}
    </div>
  );
}

/* ============================================================
   KARTU POSTER

   Sebuah <button>, bukan <div> berpenanganan klik: papan ketik
   sudah bisa menjangkaunya, Enter dan spasi sudah bekerja, dan
   pembaca layar mengumumkannya sebagai tombol - semuanya tanpa
   tambahan apa pun.

   `lebarMaks` membatasi lebar tampilnya. Tanpa itu, gambar 1080px
   melar memenuhi kolom dan tingginya ikut membengkak - masalah yang
   sama dengan poster lama, hanya lebih kecil.

   Judul lembarnya ditulis sebagai pita di bawah gambar, bukan
   ditumpuk di atasnya: poster ini sudah padat, tulisan di atasnya
   akan menabrak isinya.
   ============================================================ */

function KartuPoster({ poster, lang, onBuka, onGagal, lebarMaks = 480, ukuran, prioritas = false }) {
  return (
    <button
      type="button"
      onClick={onBuka}
      className="group block w-full overflow-hidden rounded-2xl bg-[var(--c-surface)] text-left shadow-sm ring-1 ring-[var(--c-brand-line)] transition hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
      style={{ maxWidth: lebarMaks }}
    >
      <Image
        src={poster.src}
        width={poster.lebar ?? 1080}
        height={poster.tinggi ?? 1350}
        sizes={ukuran ?? "92vw"}
        alt={pilihTeks(poster.alt, lang)}
        priority={prioritas}
        onError={onGagal}
        className="h-auto w-full transition-transform duration-500 ease-out group-hover:scale-[1.015] motion-reduce:transform-none"
      />

      <span className="flex items-center justify-between gap-3 border-t border-[var(--c-line)] px-4 py-3">
        <span className="text-[13px] font-bold text-[var(--c-ink)]">{pilihTeks(poster.judul, lang)}</span>
        <span className="text-[12.5px] font-semibold text-[var(--c-brand)]">{pilihTeks(TEKS.perbesar, lang)}</span>
      </span>
    </button>
  );
}

/* ============================================================
   PRATINJAU POSTER

   Pola yang sama dengan halaman MCU. Bisa ditutup dengan tiga cara:
   tombol silang, Esc, dan menekan latarnya. Ketiganya perlu; jendela
   yang tidak jelas cara menutupnya terasa seperti jebakan.

   Yang diurus selain tampilan:

     - gulir halaman dikunci selama jendela terbuka. Tanpa itu, roda
       tetikus menggulir halaman DI BALIK jendelanya.
     - fokus papan ketik pindah ke tombol tutup saat terbuka.
     - role="dialog" + aria-modal memberi tahu pembaca layar bahwa
       isi di belakangnya sedang tidak aktif.

   Tinggi gambarnya dibatasi 86vh dan lebarnya dibiarkan menyesuaikan
   - peramban menjaga rasionya sendiri. Poster 4:5 di layar komputer
   biasanya terbatas tingginya, bukan lebarnya.
   ============================================================ */

function Pratinjau({ daftar, indeks, lang, onTutup, onPindah }) {
  const tombolTutup = useRef(null);
  const poster = daftar[indeks];
  const banyak = daftar.length > 1;

  useEffect(() => {
    tombolTutup.current?.focus();

    const gulirSemula = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const tekan = (e) => {
      if (e.key === "Escape") onTutup();
      if (!banyak) return;
      if (e.key === "ArrowRight") onPindah((indeks + 1) % daftar.length);
      if (e.key === "ArrowLeft") onPindah((indeks - 1 + daftar.length) % daftar.length);
    };

    window.addEventListener("keydown", tekan);

    return () => {
      window.removeEventListener("keydown", tekan);
      document.body.style.overflow = gulirSemula;
    };
  }, [indeks, banyak, daftar.length, onTutup, onPindah]);

  if (!poster) return null;

  return (
    /* Menekan latar menutup; menekan gambarnya tidak - karena itu ada
       stopPropagation di kotak isinya. */
    <div
      role="dialog"
      aria-modal="true"
      aria-label={pilihTeks(poster.judul, lang)}
      onClick={onTutup}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
    >
      <div onClick={(e) => e.stopPropagation()} className="relative w-fit max-w-full">
        <Image
          src={poster.src}
          width={poster.lebar ?? 1080}
          height={poster.tinggi ?? 1350}
          sizes="92vw"
          alt={pilihTeks(poster.alt, lang)}
          className="block h-auto w-auto max-h-[86vh] max-w-full rounded-lg"
        />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[13px] font-semibold text-white/80">{pilihTeks(poster.judul, lang)}</p>

          {banyak && (
            <div className="flex items-center gap-2">
              <span className="text-[12.5px] text-white/60">{indeks + 1} / {daftar.length}</span>
              <button
                type="button"
                onClick={() => onPindah((indeks - 1 + daftar.length) % daftar.length)}
                aria-label={pilihTeks(TEKS.sebelumnya, lang)}
                className="rounded-lg bg-white/15 px-3.5 py-2 text-[13px] font-bold text-white transition-colors hover:bg-white/25"
              >
                &larr;
              </button>
              <button
                type="button"
                onClick={() => onPindah((indeks + 1) % daftar.length)}
                aria-label={pilihTeks(TEKS.berikutnya, lang)}
                className="rounded-lg bg-white/15 px-3.5 py-2 text-[13px] font-bold text-white transition-colors hover:bg-white/25"
              >
                &rarr;
              </button>
            </div>
          )}
        </div>

        <button
          ref={tombolTutup}
          type="button"
          onClick={onTutup}
          aria-label={pilihTeks(TEKS.tutup, lang)}
          className="absolute -top-3.5 -right-1.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--c-surface)] text-[var(--c-ink)] shadow-lg transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width={18} height={18}>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function IkonWhatsApp({ kecil }) {
  const u = kecil ? 14 : 20;
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width={u} height={u} className="shrink-0">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.9 9.9 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.85 9.85 0 0 0 12.04 2zm0 18.14h-.01a8.23 8.23 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.22-8.24 8.22z" />
    </svg>
  );
}

function IkonTelepon({ kecil }) {
  const u = kecil ? 14 : 20;
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width={u} height={u} className="shrink-0">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IkonPeta() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width={20} height={20} className="mt-0.5 shrink-0 text-[var(--c-brand-mid)]">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
